/**
 * Conversa aberta: cabeçalho (ações), mensagens e compositor. Arrastar um
 * arquivo para a conversa prepara o anexo.
 */
import type { ConversaResumo, MensagemDto } from "@ydchat/shared";
import {
  ROTULO_ETAPA,
  ROTULO_PRIORIDADE,
  ROTULO_STATUS_PEDIDO,
  formatarTelefone,
} from "@ydchat/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  BellRing,
  CheckCircle2,
  Megaphone,
  PanelRight,
  RotateCcw,
  ShoppingBag,
  Upload,
} from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type DragEvent } from "react";
import { Avatar } from "@/components/ui/avatar";
import { Botao } from "@/components/ui/botao";
import { Esqueleto } from "@/components/ui/esqueleto";
import { PainelLateral } from "@/components/ui/painel-lateral";
import { Selo } from "@/components/ui/selo";
import { api } from "@/lib/api";
import { useVariasCaixas } from "@/lib/caixas";
import { useAcoesCentral } from "@/lib/central";
import { cn } from "@/lib/cn";
import { formatarData, formatarHora, horaOuData } from "@/lib/formatar";
import { useVisao } from "@/lib/visao";
import { BotaoAssumir, EscolherPessoa, MenuAcoesConversa } from "./acoes-conversa";
import { Balao, rotuloTipo } from "./balao";
import { Compositor } from "./compositor";
import { STATUS_PEDIDO } from "./contexto-cliente";
import { Identificacao } from "./identificacao";
import { PainelVenda } from "./venda";
import { useAtalho } from "@/lib/atalhos";

export function Conversa({
  conversaId,
  onAlternarContexto,
}: {
  conversaId: string;
  onAlternarContexto: () => void;
}) {
  const qc = useQueryClient();
  const conversa = useQuery({
    queryKey: ["conversa", conversaId, "dados"],
    queryFn: () => api.get<ConversaResumo>(`/conversas/${conversaId}`),
  });
  const mensagens = useQuery({
    queryKey: ["conversa", conversaId, "mensagens"],
    queryFn: () => api.get<MensagemDto[]>(`/conversas/${conversaId}/mensagens`),
  });
  const [respondendoA, setRespondendoA] = useState<MensagemDto | null>(null);
  const [encaminhando, setEncaminhando] = useState<{ mensagem: MensagemDto | null } | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const [anexoSolto, setAnexoSolto] = useState<File | null>(null);
  // Painel de venda: aberto pelo cabeçalho ou por um produto achado na foto.
  const [venda, setVenda] = useState<{ busca: string } | null>(null);
  useAtalho("abrir-venda", () => setVenda({ busca: "" }));
  useAtalho("alternar-contexto", onAlternarContexto);
  const limparAnexoSolto = useCallback(() => setAnexoSolto(null), []);

  useEffect(() => {
    setRespondendoA(null);
    setEncaminhando(null);
    setVenda(null);
  }, [conversaId]);

  // Abriu a conversa: zera não lidas (e confirma leitura para a cliente).
  const naoLidas = conversa.data?.naoLidas ?? 0;
  useEffect(() => {
    if (naoLidas > 0) {
      void api
        .post(`/conversas/${conversaId}/lida`)
        .then(() => qc.invalidateQueries({ queryKey: ["conversas"] }));
    }
  }, [conversaId, naoLidas, qc]);

  if (conversa.isError) {
    // Ex.: a conversa foi transferida para outra vendedora enquanto estava aberta.
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-neutral-600">{conversa.error.message}</p>
        <Link to="/atendimentos" search={(s) => s} className="text-sm font-semibold">
          Voltar para os atendimentos
        </Link>
      </div>
    );
  }

  const soltar = (e: DragEvent) => {
    e.preventDefault();
    setArrastando(false);
    const f = e.dataTransfer.files[0];
    if (f) setAnexoSolto(f);
  };

  return (
    <div
      className="relative flex h-full min-w-0 flex-col bg-neutral-50"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("Files")) {
          e.preventDefault();
          setArrastando(true);
        }
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setArrastando(false)}
      onDrop={soltar}
    >
      <Cabecalho
        conversa={conversa.data}
        onAlternarContexto={onAlternarContexto}
        onAbrirVenda={() => setVenda({ busca: "" })}
      />
      <ListaMensagens
        conversaId={conversaId}
        onBuscarNaVenda={(busca) => setVenda({ busca })}
        mensagens={mensagens.data}
        carregando={mensagens.isPending}
        acoes={{
          onResponder: setRespondendoA,
          onEncaminhar: (m) => setEncaminhando({ mensagem: m }),
        }}
      />
      {conversa.data && (
        <PainelVenda
          conversa={conversa.data}
          aberto={!!venda}
          buscaInicial={venda?.busca}
          onFechar={() => setVenda(null)}
        />
      )}
      {conversa.data && (
        <Compositor
          conversa={conversa.data}
          respondendoA={respondendoA}
          onLimparResposta={() => setRespondendoA(null)}
          anexoExterno={anexoSolto}
          onAnexoConsumido={limparAnexoSolto}
        />
      )}
      {arrastando && (
        <div className="pointer-events-none absolute inset-2 z-30 flex items-center justify-center rounded-xl border-2 border-dashed border-gold-500 bg-gold-50/90 text-gold-800">
          <span className="flex items-center gap-2 font-semibold">
            <Upload size={20} strokeWidth={1.75} /> Solte para anexar
          </span>
        </div>
      )}
      {conversa.data && (
        <PainelEncaminhar
          conversa={conversa.data}
          mensagem={encaminhando?.mensagem ?? null}
          aberto={!!encaminhando}
          onFechar={() => setEncaminhando(null)}
        />
      )}
    </div>
  );
}

function Cabecalho({
  conversa,
  onAlternarContexto,
  onAbrirVenda,
}: {
  conversa?: ConversaResumo;
  onAlternarContexto: () => void;
  onAbrirVenda: () => void;
}) {
  const { concluir, reabrir } = useAcoesCentral();
  const variasCaixas = useVariasCaixas();
  return (
    <header className="flex min-h-16 items-center gap-2 border-b border-neutral-200 bg-white px-2 py-2 md:gap-3 md:px-4">
      <Link
        to="/atendimentos"
        search={(s) => s}
        aria-label="Voltar para a lista"
        className="flex size-10 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-100 md:hidden"
      >
        <ArrowLeft size={20} strokeWidth={1.75} />
      </Link>
      {conversa ? (
        <>
          <Avatar nome={conversa.contato.nome} className="max-sm:hidden" />
          <button onClick={onAlternarContexto} className="min-w-0 flex-1 text-left">
            <p className="truncate font-semibold text-neutral-900">{conversa.contato.nome}</p>
            <p className="truncate text-xs text-neutral-500">
              {formatarTelefone(conversa.contato.telefone)}
              {conversa.canal.comunicacao
                ? ` · ${conversa.canal.nome} (canal automático)`
                : variasCaixas && ` · ${conversa.canal.nome}`}
              {" · "}
              {conversa.responsavel ? conversa.responsavel.nome : "Sem responsável"}
              {" · "}
              {conversa.status === "encerrada" ? "Concluída" : ROTULO_ETAPA[conversa.etapa]}
            </p>
          </button>
          <div className="hidden items-center gap-1.5 lg:flex">
            {conversa.canal.comunicacao && (
              <span title="Número só de avisos: não entra na fila nem conta espera.">
                <Selo>
                  <Megaphone size={11} strokeWidth={2} /> Canal automático
                </Selo>
              </span>
            )}
            {conversa.acompanharEm && (
              <Selo tom="dourado">
                <BellRing size={11} strokeWidth={2} /> {horaOuData(conversa.acompanharEm)}{" "}
                {formatarHora(conversa.acompanharEm)}
              </Selo>
            )}
            {!conversa.contato.erpClienteId && <Selo tom="dourado">Novo cliente</Selo>}
            {conversa.etiquetas.map((e) => (
              <Selo key={e.id} cor={e.cor}>
                {e.nome}
              </Selo>
            ))}
          </div>
          {conversa.pedido && (
            <button onClick={onAbrirVenda} className="max-sm:hidden" title="Ver pedido">
              <Selo tom={STATUS_PEDIDO[conversa.pedido.status].tom}>
                #{conversa.pedido.numero} · {ROTULO_STATUS_PEDIDO[conversa.pedido.status]}
              </Selo>
            </button>
          )}
          {conversa.prioridade > 0 && (
            <Selo tom={conversa.prioridade === 2 ? "perigo" : "alerta"}>
              {ROTULO_PRIORIDADE[conversa.prioridade]}
            </Selo>
          )}
          <BotaoAssumir conversa={conversa} />
          {conversa.status === "encerrada" ? (
            <Botao
              variante="secundario"
              tamanho="sm"
              onClick={() => reabrir.mutate(conversa.id)}
              disabled={reabrir.isPending}
            >
              <RotateCcw size={15} strokeWidth={1.75} />{" "}
              <span className="max-sm:hidden">Reabrir</span>
            </Botao>
          ) : (
            conversa.responsavel && (
              <Botao
                variante="secundario"
                tamanho="sm"
                onClick={() => concluir.mutate(conversa.id)}
                disabled={concluir.isPending}
                title="Concluir atendimento"
              >
                <CheckCircle2 size={15} strokeWidth={1.75} />{" "}
                <span className="max-sm:hidden">Concluir</span>
              </Botao>
            )
          )}
          <Botao
            variante={conversa.temCarrinho ? "secundario" : "fantasma"}
            tamanho="icone"
            onClick={onAbrirVenda}
            aria-label={conversa.temCarrinho ? "Venda (carrinho em montagem)" : "Venda"}
            title="Venda"
            className="relative"
          >
            <ShoppingBag size={20} strokeWidth={1.75} />
            {conversa.temCarrinho && (
              <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-gold-500" />
            )}
          </Botao>
          <Botao
            variante="fantasma"
            tamanho="icone"
            onClick={onAlternarContexto}
            aria-label="Dados do cliente"
            title="Dados do cliente"
          >
            <PanelRight size={20} strokeWidth={1.75} />
          </Botao>
          <MenuAcoesConversa conversa={conversa} />
        </>
      ) : (
        <div className="flex flex-1 items-center gap-3">
          <Esqueleto className="size-10 rounded-full" />
          <div className="space-y-2">
            <Esqueleto className="h-3.5 w-40" />
            <Esqueleto className="h-3 w-56" />
          </div>
        </div>
      )}
    </header>
  );
}

function ListaMensagens({
  conversaId,
  onBuscarNaVenda,
  mensagens,
  carregando,
  acoes,
}: {
  conversaId: string;
  onBuscarNaVenda: (busca: string) => void;
  mensagens?: MensagemDto[];
  carregando: boolean;
  acoes: Parameters<typeof Balao>[0]["acoes"];
}) {
  const fim = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const visao = useVisao(conversaId);
  const quantidade = mensagens?.length ?? 0;

  // Rola para o fim ao abrir e quando chega mensagem, se já estava perto do fim.
  useLayoutEffect(() => {
    const el = container.current;
    if (!el) return;
    const pertoDoFim = el.scrollHeight - el.scrollTop - el.clientHeight < 240;
    if (pertoDoFim || quantidade <= 100) fim.current?.scrollIntoView({ block: "end" });
  }, [quantidade]);

  if (carregando) {
    return (
      <div className="flex-1 space-y-3 p-4">
        <Esqueleto className="h-10 w-1/2" />
        <Esqueleto className="ml-auto h-10 w-1/3" />
        <Esqueleto className="h-16 w-2/3" />
      </div>
    );
  }

  let diaAnterior = "";
  return (
    <div
      ref={container}
      className="rolagem-fina flex-1 overflow-y-auto px-3 py-4 md:px-10"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-1.5">
        {(mensagens ?? []).map((m) => {
          const dia = formatarData(m.criadoEm);
          const separador = dia !== diaAnterior;
          diaAnterior = dia;
          return (
            <div key={m.id} className="contents">
              {separador && (
                <div className="my-3 flex justify-center">
                  <span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-neutral-500 shadow-xs">
                    {dia}
                  </span>
                </div>
              )}
              <Balao mensagem={m} acoes={acoes} />
              {m.tipo === "imagem" && m.direcao === "entrada" && visao.disponivel && (
                <Identificacao
                  conversaId={conversaId}
                  mensagemId={m.id}
                  identificacao={visao.porMensagem.get(m.id) ?? null}
                  analisando={visao.analisando.has(m.id)}
                  onBuscarNaVenda={onBuscarNaVenda}
                />
              )}
            </div>
          );
        })}
        <div ref={fim} />
      </div>
    </div>
  );
}

/** Chama alguém da equipe para a conversa (nota interna + acesso + aviso). */
function PainelEncaminhar({
  conversa,
  mensagem,
  aberto,
  onFechar,
}: {
  conversa: ConversaResumo;
  mensagem: MensagemDto | null;
  aberto: boolean;
  onFechar: () => void;
}) {
  const { encaminhar } = useAcoesCentral();
  const [comentario, setComentario] = useState("");
  const [faltaComentario, setFaltaComentario] = useState(false);
  useEffect(() => {
    if (aberto) {
      setComentario("");
      setFaltaComentario(false);
    }
  }, [aberto]);

  return (
    <PainelLateral aberto={aberto} onFechar={onFechar} titulo="Chamar alguém da equipe">
      <div className="space-y-4">
        <p className="text-sm text-neutral-600">
          A pessoa recebe um aviso e passa a ver esta conversa. A cliente não vê nada.
        </p>
        {mensagem && (
          <div className="rounded-md border-l-[3px] border-gold-400 bg-neutral-50 px-2.5 py-1.5 text-[13px] text-neutral-600">
            {mensagem.texto || rotuloTipo(mensagem.tipo)}
          </div>
        )}
        <label className="block space-y-1.5">
          <span className="block text-sm font-semibold text-neutral-800">
            O que a pessoa precisa saber
          </span>
          <textarea
            value={comentario}
            onChange={(e) => {
              setComentario(e.target.value);
              setFaltaComentario(false);
            }}
            rows={3}
            maxLength={1000}
            autoFocus
            placeholder="Ex.: pode confirmar se o Lis G chega sexta?"
            className={cn(
              "w-full rounded-md border px-3 py-2 text-sm shadow-xs",
              faltaComentario ? "border-danger-500" : "border-neutral-200",
            )}
          />
          {faltaComentario && (
            <span className="text-xs text-danger-500">
              Escreva o comentário antes de escolher a pessoa.
            </span>
          )}
        </label>
        <div>
          <p className="mb-1 text-sm font-semibold text-neutral-800">Para quem</p>
          <div className="rounded-lg border border-neutral-200 p-1">
            <EscolherPessoa
              excluir={null}
              semMotivo
              onEscolher={(p) => {
                if (!comentario.trim()) {
                  setFaltaComentario(true);
                  return;
                }
                encaminhar.mutate({
                  id: conversa.id,
                  usuarioId: p.id,
                  nome: p.nome,
                  comentario: comentario.trim(),
                  mensagemId: mensagem?.id,
                });
                onFechar();
              }}
            />
          </div>
        </div>
      </div>
    </PainelLateral>
  );
}
